/** Notification change history: change list (left) + the selected change's
 *  field-level before/after diff or raw snapshots (right), and client-side
 *  restore — "load state BEFORE/AFTER this change into the editor" applies the
 *  snapshot to the page draft; saving then records a new attributed change.
 *  Mirrors PresetHistoryDialog's layout.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  getChange,
  listChanges,
  type ChangeAction,
  type NotificationChangeDetail,
  type NotificationSnapshot,
} from '@/api/notificationChanges';
import { CHANGE_SOURCE_LABELS } from '@/api/notifications';
import { AppDialog } from '@/components/AppDialog';
import { ChangeSetChip } from '@/components/History/ChangeSetChip';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { JsonEditorWidget } from '@/components/JsonEditor/JsonEditorWidget';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import { formatRelativeTime } from '@/utils/format';

import { ALL_TEMPLATE_FIELDS } from './delivery/channelFields';
import { userActions } from './notificationLinks';
import { TextDiffView } from './TextDiffView';
import type { NotificationDraft } from './useNotificationDraft';

const ACTION_COLOR: Record<ChangeAction, 'success' | 'info' | 'error'> = {
  created: 'success',
  updated: 'info',
  deleted: 'error',
};

/** Whole-section paths diffed as single values (their rows have no stable
 *  editable sub-identity) — resolved to the section and shown as a JSON diff. */
const SECTION_PATHS = new Set([
  'block_refs',
  'delivery_methods',
  'rules',
  'advanced_rules',
  'reactions',
  'auto_replies',
]);

/** Resolve a changed path against a snapshot (dm rows matched by id). */
export function getAtPath(snapshot: NotificationSnapshot, path: string): unknown {
  if (!snapshot) return undefined;
  if (SECTION_PATHS.has(path)) {
    return (snapshot as unknown as Record<string, unknown>)[path];
  }
  if (path === 'notification') return snapshot.notification;

  const dmMatch = /^delivery_methods\[(-?\d+)\](?:\.(.+))?$/.exec(path);
  if (dmMatch) {
    const dm = (snapshot.delivery_methods ?? []).find(
      (d) => Number((d as { id?: unknown }).id) === Number(dmMatch[1]),
    );
    if (!dm) return undefined;
    return dmMatch[2] ? dm[dmMatch[2]] : dm;
  }

  if (path.startsWith('notification.')) {
    return (snapshot.notification ?? {})[path.slice('notification.'.length)];
  }
  return undefined;
}

function isTemplatePath(path: string, before: unknown, after: unknown): boolean {
  const field = path.split('.').pop() ?? '';
  if (ALL_TEMPLATE_FIELDS.includes(field)) return true;
  const long = (v: unknown) => typeof v === 'string' && v.length > 120;
  return long(before) || long(after);
}

function shortValue(v: unknown): string {
  if (v === undefined) return '—';
  if (v === null) return 'null';
  if (typeof v === 'string') return v === '' ? '""' : v;
  return JSON.stringify(v);
}

interface Props {
  open: boolean;
  notificationId: number;
  draft: NotificationDraft;
  onClose: () => void;
  /** Called after a snapshot was applied to the draft (comment for the save dialog). */
  onRestored: (comment: string) => void;
}

export function NotificationHistoryDialog({ open, notificationId, draft, onClose, onRestored }: Props) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [view, setView] = useState<'fields' | 'raw'>('fields');
  const [confirmRestore, setConfirmRestore] = useState<'before' | 'after' | null>(null);

  const historyQuery = useQuery({
    queryKey: ['notification-changes', notificationId],
    queryFn: () => listChanges({ notification: notificationId, page_size: 50 }),
    enabled: open,
  });
  const changes = historyQuery.data?.results ?? [];
  const effectiveId = selectedId ?? changes[0]?.id ?? null;

  const changeQuery = useQuery({
    queryKey: ['notification-change', effectiveId],
    queryFn: () => getChange(effectiveId as number),
    enabled: open && effectiveId != null,
  });
  const change = changeQuery.data;

  const applySnapshot = (which: 'before' | 'after', ch: NotificationChangeDetail) => {
    const snapshot = which === 'before' ? ch.state_before : ch.state_after;
    if (!snapshot) return;
    draft.applySnapshot(snapshot);
    onRestored(`Restored from change #${ch.id} (state ${which})`);
    onClose();
  };

  const requestRestore = (which: 'before' | 'after') => {
    if (draft.dirty) setConfirmRestore(which);
    else if (change) applySnapshot(which, change);
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={`Notification #${notificationId} — change history`}
      size="lg"
      actions={<Button onClick={onClose}>Close</Button>}
    >
      {historyQuery.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {historyQuery.isError && <Alert severity="error">Couldn’t load the history.</Alert>}
      {!historyQuery.isLoading && !historyQuery.isError && changes.length === 0 && (
        <Typography color="text.secondary">No recorded changes.</Typography>
      )}

      {changes.length > 0 && (
        <Stack direction="row" spacing={2} sx={{ minHeight: 440 }}>
          <List dense sx={{ width: 300, flexShrink: 0, overflow: 'auto', maxHeight: 520 }}>
            {changes.map((c) => (
              <ListItemButton
                key={c.id}
                dense
                selected={c.id === effectiveId}
                onClick={() => setSelectedId(c.id)}
                sx={{ alignItems: 'flex-start' }}
              >
                <ListItemText
                  disableTypography
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Chip size="small" color={ACTION_COLOR[c.action]} label={c.action} />
                      <Typography variant="caption" color="text.secondary">
                        <ValueActions
                          actions={[
                            act.copy(String(c.id), 'Copy change id'),
                            // The correlation id is on every row and shown
                            // nowhere — it is what ties this edit to the rest
                            // of the request that made it.
                            ...(c.correlation_id
                              ? [act.copy(c.correlation_id, 'Copy correlation id')]
                              : []),
                          ]}
                        >
                          #{c.id}
                        </ValueActions>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatRelativeTime(c.created_ts)}
                      </Typography>
                      <ChangeSetChip changeSet={c.change_set} />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.25 }}>
                      <Typography variant="caption" component="div" noWrap>
                        {c.actor ? (
                          <ValueActions actions={userActions(c.actor)}>
                            {c.actor.name || c.actor.email}
                          </ValueActions>
                        ) : (
                          'unknown actor'
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="div" noWrap>
                        {CHANGE_SOURCE_LABELS[c.source] ?? c.source}
                        {c.ip ? (
                          <>
                            {' · '}
                            <ValueActions
                              actions={[act.whois(c.ip), act.copy(c.ip, 'Copy IP')]}
                            >
                              {c.ip}
                            </ValueActions>
                          </>
                        ) : (
                          ''
                        )}
                      </Typography>
                      {c.comment && (
                        <Tooltip title={c.comment}>
                          <Typography variant="caption" component="div" noWrap sx={{ fontStyle: 'italic' }}>
                            “{c.comment}”
                          </Typography>
                        </Tooltip>
                      )}
                      {c.changed_paths.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                          {c.changed_paths.slice(0, 3).map((p) => (
                            <Chip
                              key={p}
                              size="small"
                              variant="outlined"
                              label={p}
                              sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: 10, fontFamily: 'monospace' } }}
                            />
                          ))}
                          {c.changed_paths.length > 3 && (
                            <Typography variant="caption" color="text.secondary">
                              +{c.changed_paths.length - 3}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </List>

          <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            {changeQuery.isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            )}
            {changeQuery.isError && <Alert severity="error">Couldn’t load the change.</Alert>}
            {change && (
              <>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={view}
                    onChange={(_, v: 'fields' | 'raw' | null) => v && setView(v)}
                  >
                    <ToggleButton value="fields">Fields</ToggleButton>
                    <ToggleButton value="raw">Raw</ToggleButton>
                  </ToggleButtonGroup>
                  <Typography variant="caption" color="text.secondary">
                    {change.changed_paths.length} changed path
                    {change.changed_paths.length === 1 ? '' : 's'}
                  </Typography>
                </Stack>

                {view === 'fields' ? (
                  <Box sx={{ overflow: 'auto', maxHeight: 420 }}>
                    {change.changed_paths.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                        No field-level paths recorded ({change.action}) — see the Raw view.
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 220 }}>Path</TableCell>
                            <TableCell>Before → After</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {change.changed_paths.map((path) => {
                            const before = getAtPath(change.state_before, path);
                            const after = getAtPath(change.state_after, path);
                            // Whole sections are arrays/objects — pretty-print
                            // them so the line diff shows what actually changed.
                            const isSection = SECTION_PATHS.has(path);
                            const asDiff = isSection || isTemplatePath(path, before, after);
                            const diffText = (v: unknown) =>
                              typeof v === 'string'
                                ? v
                                : isSection && v !== undefined
                                  ? JSON.stringify(v, null, 2)
                                  : shortValue(v);
                            return (
                              <TableRow key={path}>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, verticalAlign: 'top' }}>
                                  {path}
                                </TableCell>
                                <TableCell>
                                  {asDiff ? (
                                    <TextDiffView
                                      before={diffText(before)}
                                      after={diffText(after)}
                                      maxHeight={260}
                                    />
                                  ) : (
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-word' }}>
                                      {shortValue(before)}
                                      {'  →  '}
                                      {shortValue(after)}
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </Box>
                ) : (
                  <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
                    {(
                      [
                        ['Before', change.state_before],
                        ['After', change.state_after],
                      ] as const
                    ).map(([label, state]) => (
                      <Box key={label} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary">
                          {label}
                        </Typography>
                        <Box sx={{ height: 380 }}>
                          {state ? (
                            // key forces a remount per change — the widget
                            // initializes once by design.
                            <JsonEditorWidget
                              key={`${change.id}-${label}`}
                              initialValue={state as unknown as Record<string, unknown>}
                              readOnly
                              mode="tree"
                            />
                          ) : (
                            <Typography color="text.secondary" sx={{ p: 2 }}>
                              — none ({label === 'Before' ? 'creation' : 'deletion'}) —
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}

                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    disabled={!change.state_before}
                    onClick={() => requestRestore('before')}
                  >
                    Load state BEFORE this change into the editor
                  </Button>
                  <Button
                    size="small"
                    disabled={!change.state_after}
                    onClick={() => requestRestore('after')}
                  >
                    Load state AFTER
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
      )}

      <ConfirmDialog
        open={confirmRestore !== null}
        title="Replace your unsaved changes?"
        message="The editor has unsaved changes. Loading this snapshot will replace them (nothing is saved until you press Save)."
        confirmLabel="Load snapshot"
        onConfirm={() => {
          if (confirmRestore && change) applySnapshot(confirmRestore, change);
          setConfirmRestore(null);
        }}
        onCancel={() => setConfirmRestore(null)}
      />
    </AppDialog>
  );
}
